/**
 * The Policy Library: GiveHere's controlled security & compliance documents (Word + PDF), shared
 * with processors, banks and auditors under NDA.
 *
 *   GET /v1/admin/policies                     platform staff — the manifest (documents list)
 *   GET /v1/admin/policies/:id/:format         platform staff, audited — one file's bytes
 *
 * The set includes internal network architecture and an open-gap register, so it is never compiled
 * into a client bundle and never served as a static file: the API reads it from
 * `services/api/policies/` (written by `scripts/build-policies.mjs`) and hands it only to an
 * authenticated platform staff session.
 */
import { z } from "zod";
/** `GH-POL-001`, `GH-STD-004`: the only shape a document id may take, on disk or in a path. */
export const POLICY_ID_PATTERN = /^GH-[A-Z]{3}-\d{3}$/;
export const policyIdSchema = z.string().regex(POLICY_ID_PATTERN);
export const POLICY_FORMATS = ["docx", "pdf"];
export const policyFormatSchema = z.enum(POLICY_FORMATS);
export const POLICY_STATUSES = ["Draft for approval", "Approved"];
/** A file name as the generator writes it: no directory part, no leading dot. */
const policyFileNameSchema = z
    .string()
    .regex(/^GH-[A-Z]{3}-\d{3}[A-Za-z0-9._-]*\.(docx|pdf)$/)
    .max(200);
export const policyFileSchema = z.object({
    name: policyFileNameSchema,
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    bytes: z.number().int().nonnegative(),
});
export const policyDocumentSchema = z.object({
    id: policyIdSchema,
    title: z.string().min(1).max(300),
    version: z.string().min(1).max(40),
    effective: z.string().min(1).max(40),
    classification: z.string().max(120),
    owner: z.string().max(200),
    status: z.enum(POLICY_STATUSES),
    files: z.object({ docx: policyFileSchema, pdf: policyFileSchema }),
});
/** `manifest.json` on disk, and the list route's response — the same shape. */
export const policyManifestSchema = z.object({
    generatedAt: z.string().nullable(),
    documents: z.array(policyDocumentSchema),
});
export const POLICY_CONTENT_TYPES = {
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    pdf: "application/pdf",
};
//# sourceMappingURL=policies.js.map